'use client';

import React from 'react';
import Link from 'next/link';
import HealthBadge from '@/components/ui/HealthBadge';

type HealthStatus = 'healthy' | 'at-risk' | 'critical' | 'recovering' | 'optimized';

interface BVO {
  id: string;
  name: string;
  type: string;
  healthScore: number;
  conformance: number;
  valueAtRisk: string;
  status: HealthStatus;
  trend: 'up' | 'down' | 'stable';
  lastUpdated: string;
}

const bvos: BVO[] = [
  {
    id: 'bvo-financial-close',
    name: 'Financial Close',
    type: 'Finance',
    healthScore: 82,
    conformance: 91,
    valueAtRisk: '$0',
    status: 'healthy',
    trend: 'up',
    lastUpdated: '2 min ago',
  },
  {
    id: 'bvo-cash-application',
    name: 'Cash Application',
    type: 'Finance',
    healthScore: 61,
    conformance: 74,
    valueAtRisk: '$2.4M',
    status: 'at-risk',
    trend: 'down',
    lastUpdated: '4 min ago',
  },
  {
    id: 'bvo-customer-order',
    name: 'Customer Order',
    type: 'Operations',
    healthScore: 44,
    conformance: 63,
    valueAtRisk: '$5.1M',
    status: 'critical',
    trend: 'down',
    lastUpdated: '1 min ago',
  },
  {
    id: 'bvo-supplier-invoice',
    name: 'Supplier Invoice',
    type: 'Procurement',
    healthScore: 78,
    conformance: 87,
    valueAtRisk: '$340K',
    status: 'recovering',
    trend: 'up',
    lastUpdated: '6 min ago',
  },
  {
    id: 'bvo-inventory-health',
    name: 'Inventory Health',
    type: 'Operations',
    healthScore: 91,
    conformance: 95,
    valueAtRisk: '$0',
    status: 'optimized',
    trend: 'stable',
    lastUpdated: '3 min ago',
  },
  {
    id: 'bvo-working-capital',
    name: 'Working Capital',
    type: 'Finance',
    healthScore: 56,
    conformance: 69,
    valueAtRisk: '$8.7M',
    status: 'at-risk',
    trend: 'down',
    lastUpdated: '5 min ago',
  },
];

function HealthRing({ score, status }: { score: number; status: HealthStatus }) {
  const radius = 22;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;
  const strokeColor =
    status === 'healthy' || status === 'optimized' ? 'var(--positive)' :
    status === 'at-risk' ? 'var(--warning)' :
    status === 'critical' ? 'var(--negative)' : 'var(--primary)';

  return (
    <div className="relative w-14 h-14 shrink-0">
      <svg width="56" height="56" viewBox="0 0 56 56" className="-rotate-90">
        <circle cx="28" cy="28" r={radius} fill="none" stroke="var(--muted)" strokeWidth="3.5" />
        <circle
          cx="28"
          cy="28"
          r={radius}
          fill="none"
          stroke={strokeColor}
          strokeWidth="3.5"
          strokeDasharray={`${progress} ${circumference}`}
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 0.6s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs font-bold text-foreground font-tabular">{score}</span>
      </div>
    </div>
  );
}

export default function BVOHealthGrid() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-6 gap-3">
      {bvos.map((bvo) => (
        <Link
          key={bvo.id}
          href="/business-value-objects"
          className="bg-card rounded-xl p-4 card-glow hover:bg-muted/30 transition-all duration-150 group block"
        >
          <div className="flex items-start gap-3">
            <HealthRing score={bvo.healthScore} status={bvo.status} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                {bvo.name}
              </p>
              <p className="text-xs text-muted-foreground mb-2">{bvo.type}</p>
              <HealthBadge status={bvo.status} size="sm" />
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div>
              <p className="text-2xs text-muted-foreground">Conformance</p>
              <p className="text-xs font-semibold text-foreground font-tabular">{bvo.conformance}%</p>
            </div>
            <div>
              <p className="text-2xs text-muted-foreground">Value at Risk</p>
              <p className={`text-xs font-semibold font-tabular ${bvo.valueAtRisk === '$0' ? 'text-positive' : 'text-negative'}`}>
                {bvo.valueAtRisk}
              </p>
            </div>
          </div>
          <p className="text-2xs text-muted-foreground mt-2">Updated {bvo.lastUpdated}</p>
        </Link>
      ))}
    </div>
  );
}